import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, CheckCircle2, CreditCard, Crown, Loader2, Power, Search } from 'lucide-react';
import styles from './CardAllotment.module.css';
import { useStore } from '../../context/StoreContext';

// =============================================================================
// Card allotment + discount membership activation.
// Opens with a customer already loaded when the URL has ?phone=9876543210
// (links from the bill screen and membership desk add it), so nobody types the number twice.
// =============================================================================
const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001')
    .replace(/\/api\/v1(?:\/public)?\/?$/, '')
    .replace(/\/+$/, '');
const CUSTOMER_API = `${API_ORIGIN}/api/v1/smiify/sm/customers`;
const PREPAID_API = `${API_ORIGIN}/api/v1/smiify/sm/prepaid-memberships`;

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const spacedCard = (num) => String(num || '').replace(/\s+/g, '').match(/.{1,4}/g)?.join(' ') || String(num || '');

const CardAllotment = () => {
    const { activeStore } = useStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const token = localStorage.getItem('smiify_token');
    const storeId = activeStore?._id || activeStore?.id;

    const [phone, setPhone] = useState(() => (searchParams.get('phone') || '').replace(/\D/g, '').slice(-10));
    const [isSearching, setIsSearching] = useState(false);
    const [customer, setCustomer] = useState(null);
    const [message, setMessage] = useState(null); // { type: 'ok' | 'error', text }

    const [cardsByMembership, setCardsByMembership] = useState({}); // membershipId -> { loading, list }
    const [selected, setSelected] = useState({});
    const [busyId, setBusyId] = useState('');

    // The item the user came for (from the bill screen) is highlighted
    const focusId = searchParams.get('activate') || searchParams.get('allot') || '';
    const autoSearched = useRef(false);

    const headers = useCallback(
        (json = false) => ({ ...(json ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${token}`, 'x-store-id': storeId }),
        [token, storeId]
    );

    const loadCards = useCallback(async (membership) => {
        const id = membership._id || membership.id;
        const templateId = membership.templateId || membership.planId;
        if (!templateId) return;
        setCardsByMembership((m) => ({ ...m, [id]: { loading: true, list: [] } }));
        try {
            const res = await fetch(`${PREPAID_API}/cards/available/${templateId}`, { headers: headers() });
            const json = await res.json().catch(() => ({}));
            const list = (json.data || []).filter((c) => !c.status || c.status === 'AVAILABLE');
            setCardsByMembership((m) => ({ ...m, [id]: { loading: false, list } }));
            setSelected((s) => ({ ...s, [id]: s[id] || list[0]?.cardNumber || '' }));
        } catch {
            setCardsByMembership((m) => ({ ...m, [id]: { loading: false, list: [] } }));
        }
    }, [headers]);

    const applyCustomer = useCallback((found) => {
        setCustomer(found);
        (found?.prepaidMemberships || []).filter((m) => m.status === 'PENDING_ALLOTMENT').forEach(loadCards);
    }, [loadCards]);

    const search = useCallback(async (number) => {
        const clean = String(number || '').replace(/\D/g, '');
        if (!storeId) { setMessage({ type: 'error', text: 'Select a store first.' }); return; }
        if (clean.length !== 10) { setMessage({ type: 'error', text: 'Enter a valid 10-digit mobile number.' }); return; }

        setIsSearching(true);
        setMessage(null);
        setCustomer(null);
        setCardsByMembership({});
        setSelected({});
        try {
            const res = await fetch(`${CUSTOMER_API}/search?query=${clean}`, { headers: headers() });
            const json = await res.json().catch(() => ({}));
            const found = (json.data || []).find((c) => c.phone === clean) || (json.data || [])[0];
            if (found) applyCustomer(found);
            else setMessage({ type: 'error', text: 'No customer found with this number in this store.' });
        } catch {
            setMessage({ type: 'error', text: 'Could not reach the server. Check your connection.' });
        } finally {
            setIsSearching(false);
        }
    }, [storeId, headers, applyCustomer]);

    // Auto-search when opened with ?phone=
    useEffect(() => {
        if (autoSearched.current || !storeId || phone.length !== 10 || !searchParams.get('phone')) return;
        autoSearched.current = true;
        search(phone);
    }, [storeId, phone, search, searchParams]);

    const post = async (path, body) => {
        const res = await fetch(`${CUSTOMER_API}${path}`, { method: 'POST', headers: headers(true), body: JSON.stringify(body) });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) throw new Error(json.message || 'Request failed.');
        return json;
    };

    const allot = async (membershipId) => {
        const cardNumber = String(selected[membershipId] || '').replace(/\s+/g, '');
        if (cardNumber.length !== 16 || busyId) return;
        setBusyId(membershipId);
        setMessage(null);
        try {
            const json = await post('/allot-card', { customerId: customer._id || customer.id, membershipId, cardNumber });
            setMessage({ type: 'ok', text: json.message || 'Card allotted.' });
            if (json.data) applyCustomer(json.data);
        } catch (e) {
            setMessage({ type: 'error', text: e.message });
        } finally {
            setBusyId('');
        }
    };

    const activate = async (membershipId) => {
        if (busyId) return;
        setBusyId(membershipId);
        setMessage(null);
        try {
            const json = await post('/activate-discount-membership', { customerId: customer._id || customer.id, membershipId });
            setMessage({ type: 'ok', text: json.message || 'Membership activated.' });
            if (json.data) setCustomer((prev) => ({ ...prev, ...json.data }));
        } catch (e) {
            setMessage({ type: 'error', text: e.message });
        } finally {
            setBusyId('');
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setSearchParams(phone ? { phone } : {}, { replace: true });
        search(phone);
    };

    const pendingCards = (customer?.prepaidMemberships || []).filter((m) => m.status === 'PENDING_ALLOTMENT');
    const pendingDiscounts = (customer?.discountMemberships || []).filter((m) => m.status === 'INACTIVE');
    const nothingPending = customer && !pendingCards.length && !pendingDiscounts.length;

    return (
        <div className={styles.page}>
            <header className={styles.head}>
                <span className={styles.headIcon}><Crown size={16} /></span>
                <div>
                    <h1>Membership activation</h1>
                    <p>Allot physical prepaid cards and activate discount memberships.</p>
                </div>
            </header>

            <form className={styles.searchRow} onSubmit={onSubmit}>
                <div className={styles.searchBox}>
                    <Search size={14} />
                    <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="Client mobile number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        aria-label="Client mobile number"
                    />
                </div>
                <button type="submit" className={styles.primaryBtn} disabled={isSearching}>
                    {isSearching ? <Loader2 size={14} className={styles.spin} /> : 'Search'}
                </button>
            </form>

            {message && (
                <div className={message.type === 'ok' ? styles.ok : styles.error} role="status">
                    {message.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {message.text}
                </div>
            )}

            {isSearching && !customer && (
                <div className={styles.muted}><Loader2 size={14} className={styles.spin} /> Finding client...</div>
            )}

            {customer && (
                <section className={styles.panel}>
                    <div className={styles.client}>
                        <span className={styles.avatar}>{String(customer.name || '?').charAt(0).toUpperCase()}</span>
                        <div>
                            <strong>{customer.name}</strong>
                            <span>+91 {customer.phone}</span>
                        </div>
                    </div>

                    {nothingPending && (
                        <div className={styles.empty}>
                            <CheckCircle2 size={22} />
                            <strong>All clear</strong>
                            <span>No cards to allot or memberships to activate.</span>
                        </div>
                    )}

                    {pendingDiscounts.length > 0 && (
                        <div className={styles.group}>
                            <h2>Discount memberships to activate <em>{pendingDiscounts.length}</em></h2>
                            {pendingDiscounts.map((m) => {
                                const id = m._id || m.id;
                                return (
                                    <div key={id} className={`${styles.item} ${focusId === id ? styles.itemFocus : ''}`}>
                                        <span className={styles.itemIcon}><Crown size={14} /></span>
                                        <div className={styles.itemMain}>
                                            <strong>{m.name || 'Discount membership'}</strong>
                                            <span>Bought {fmtDate(m.purchaseDate)}, valid till {fmtDate(m.expiryDate)}</span>
                                        </div>
                                        <button type="button" className={styles.primaryBtn} onClick={() => activate(id)} disabled={Boolean(busyId)}>
                                            {busyId === id ? <Loader2 size={13} className={styles.spin} /> : <Power size={13} />} Activate
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {pendingCards.length > 0 && (
                        <div className={styles.group}>
                            <h2>Prepaid cards to allot <em>{pendingCards.length}</em></h2>
                            {pendingCards.map((m) => {
                                const id = m._id || m.id;
                                const state = cardsByMembership[id] || { loading: true, list: [] };
                                return (
                                    <div key={id} className={`${styles.item} ${styles.itemWrap} ${focusId === id ? styles.itemFocus : ''}`}>
                                        <span className={styles.itemIcon}><CreditCard size={14} /></span>
                                        <div className={styles.itemMain}>
                                            <strong>{m.name || 'Prepaid card'}</strong>
                                            <span>Paid {fmtDate(m.purchaseDate)}</span>
                                        </div>
                                        <div className={styles.allot}>
                                            <select
                                                value={selected[id] || ''}
                                                onChange={(e) => setSelected((s) => ({ ...s, [id]: e.target.value }))}
                                                disabled={state.loading || state.list.length === 0}
                                                aria-label={`Card for ${m.name || 'prepaid card'}`}
                                            >
                                                {state.loading && <option value="">Loading cards...</option>}
                                                {!state.loading && state.list.length === 0 && <option value="">No free cards for this plan</option>}
                                                {state.list.map((c) => <option key={c.cardNumber} value={c.cardNumber}>{spacedCard(c.cardNumber)}</option>)}
                                            </select>
                                            <button type="button" className={styles.primaryBtn} onClick={() => allot(id)} disabled={Boolean(busyId) || !selected[id]}>
                                                {busyId === id ? <Loader2 size={13} className={styles.spin} /> : <Check size={13} />} Allot
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
};

export default CardAllotment;